/**
 * BIRHAUS Financial Theme
 * 
 * Specialized theme for financial management systems like church administration,
 * donation tracking, expense management, and financial reporting.
 * 
 * Design principles:
 * - Trust and transparency through honest data presentation
 * - Professional appearance for formal financial contexts
 * - High contrast for accessibility and precision
 * - Spanish-first labeling for Latin American markets
 * - Conservative color palette for financial credibility
 * - Clear visual hierarchy for financial data
 */

export interface BirhausThemeConfig {
  // Theme metadata
  name: string
  version: string
  description: string
  industry: string
  
  // Color palette following BIRHAUS principle #9: Consistency via tokens
  colors: {
    // Primary colors (single accent principle)
    primary: string
    primaryHover: string
    primaryDisabled: string
    
    // Semantic colors for financial states
    success: string
    successBackground: string
    warning: string
    warningBackground: string
    error: string
    errorBackground: string
    info: string
    infoBackground: string
    
    // Neutral colors
    background: string
    surface: string
    surfaceElevated: string
    border: string
    borderSubtle: string
    
    // Text colors (high contrast for accessibility)
    textPrimary: string
    textSecondary: string
    textDisabled: string
    textInverse: string
    
    // Financial-specific colors
    income: string
    expense: string
    transfer: string
    pending: string
    approved: string
    rejected: string
  }
  
  // v3.0 Glass Morphism Configuration
  glass?: {
    // Glass effect colors with opacity support
    primary: string
    secondary: string
    surface: string
    border: string
    // Glass blur settings
    blurIntensity: {
      light: number
      medium: number
      heavy: number
    }
    // Background saturation enhancement
    saturation: number
    // Opacity levels for different glass variants
    opacity: {
      subtle: number
      light: number
      medium: number
      heavy: number
      ultra: number
    }
  }
  
  // Typography scale optimized for financial data
  typography: {
    fontFamily: {
      sans: string[]
      mono: string[]
    }
    fontSize: {
      xs: string
      sm: string
      base: string
      lg: string
      xl: string
      '2xl': string
      '3xl': string
      '4xl': string
    }
    fontWeight: {
      normal: number
      medium: number
      semibold: number
      bold: number
    }
    lineHeight: {
      tight: number
      normal: number
      relaxed: number
    }
  }
  
  // Spacing following 8pt grid system
  spacing: {
    '0': string
    '1': string  // 4px
    '2': string  // 8px
    '3': string  // 12px
    '4': string  // 16px
    '5': string  // 20px
    '6': string  // 24px
    '8': string  // 32px
    '10': string // 40px
    '12': string // 48px
    '16': string // 64px
    '20': string // 80px
    '24': string // 96px
    '32': string // 128px
  }
  
  // v3.0 Generous Spacing Configuration (2x traditional)
  spacingV3?: {
    none: string  // 0px
    xs: string    // 8px
    sm: string    // 16px
    md: string    // 24px
    lg: string    // 32px
    xl: string    // 48px
    '2xl': string // 64px
    '3xl': string // 96px
    // Component-specific generous spacing
    cardPadding: string
    buttonPadding: string
    inputPadding: string
    sectionMargin: string
  }
  
  // Border radius for consistency
  borderRadius: {
    none: string
    sm: string
    base: string
    lg: string
    xl: string
    full: string
  }
  
  // v3.0 Radical Border Radius Configuration
  borderRadiusV3?: {
    // Radical border radius scale (28px+)
    xs: string      // 8px
    sm: string      // 12px
    md: string      // 16px
    lg: string      // 24px
    xl: string      // 32px
    '2xl': string   // 40px
    '3xl': string   // 48px
    circle: string  // 50%
    pill: string    // 9999px
    // Custom values for specific components
    card: string
    button: string
    input: string
    dialog: string
  }
  
  // Shadows for depth perception
  boxShadow: {
    sm: string
    base: string
    lg: string
    xl: string
  }
  
  // v3.0 Enhanced Shadows with Glass Effects
  shadowV3?: {
    none: string
    sm: string
    md: string
    lg: string
    xl: string
    glass: string        // Glass morphism shadow
    floating: string     // Floating element shadow
    elevated: string     // Elevated card shadow
    // Component-specific shadows
    button: string
    card: string
    dialog: string
    tooltip: string
  }
  
  // v3.0 Animation Configuration
  animationV3?: {
    // Animation duration presets
    duration: {
      instant: string   // 0ms
      fast: string      // 150ms
      base: string      // 300ms
      slow: string      // 500ms
      slower: string    // 750ms
      slowest: string   // 1000ms
    }
    // Animation easing presets
    easing: {
      linear: string
      smooth: string    // ease-in-out
      snappy: string    // cubic-bezier(0.4, 0, 0.2, 1)
      bounce: string    // cubic-bezier(0.68, -0.55, 0.265, 1.55)
      gentle: string    // cubic-bezier(0.25, 0.8, 0.25, 1)
      entrance: string  // cubic-bezier(0, 0, 0.2, 1)
      exit: string      // cubic-bezier(0.4, 0, 1, 1)
    }
    // GPU acceleration settings
    useGPU: boolean
    // Performance mode settings
    respectReducedMotion: boolean
  }
  
  // Financial-specific component styles
  components: {
    // Currency display
    currency: {
      positive: string
      negative: string
      neutral: string
      fontSize: string
      fontFamily: string
      fontWeight: number
    }
    
    // Financial status badges
    statusBadge: {
      pending: { bg: string; text: string; border: string }
      approved: { bg: string; text: string; border: string }
      rejected: { bg: string; text: string; border: string }
      transferred: { bg: string; text: string; border: string }
      received: { bg: string; text: string; border: string }
    }
    
    // Data tables for financial reports
    dataTable: {
      headerBg: string
      headerText: string
      rowBg: string
      rowBgAlt: string
      rowBorder: string
      textAlign: string
    }
    
    // Form elements for financial input
    form: {
      inputBg: string
      inputBorder: string
      inputFocusBorder: string
      labelText: string
      errorText: string
      successText: string
    }
    
    // Cards for financial summaries
    card: {
      bg: string
      border: string
      shadow: string
      headerBg: string
      padding: string
    }
  }
  
  // Spanish-first labels (BIRHAUS Principle #7)
  labels: {
    es: {
      // Common financial terms
      amount: string
      balance: string
      income: string
      expense: string
      transfer: string
      date: string
      status: string
      pending: string
      approved: string
      rejected: string
      total: string
      subtotal: string
      tax: string
      
      // Actions
      save: string
      cancel: string
      edit: string
      delete: string
      approve: string
      reject: string
      transferAction: string
      export: string
      print: string
      
      // Navigation
      dashboard: string
      transactions: string
      reports: string
      settings: string
      users: string
      
      // Messages
      success: string
      error: string
      warning: string
      loading: string
      noData: string
    }
    en?: {
      // English fallbacks
      amount: string
      balance: string
      income: string
      expense: string
      transfer: string
      date: string
      status: string
      pending: string
      approved: string
      rejected: string
      total: string
      subtotal: string
      tax: string
      
      save: string
      cancel: string
      edit: string
      delete: string
      approve: string
      reject: string
      transferAction: string
      export: string
      print: string
      
      dashboard: string
      transactions: string
      reports: string
      settings: string
      users: string
      
      success: string
      error: string
      warning: string
      loading: string
      noData: string
    }
  }
}

export const financialTheme: BirhausThemeConfig = {
  name: 'BIRHAUS Financial',
  version: '3.0.0',
  description: 'v3.0 Radical Minimalist theme for financial management systems, church administration, and donation tracking',
  industry: 'financial',
  
  colors: {
    // Primary: Conservative blue for trust and professionalism
    primary: '#1F6FEB',
    primaryHover: '#0969DA',
    primaryDisabled: '#8DA4CC',
    
    // Semantic colors optimized for financial data
    success: '#10B981',
    successBackground: '#ECFDF5',
    warning: '#F59E0B',
    warningBackground: '#FFFBEB',
    error: '#EF4444',
    errorBackground: '#FEF2F2',
    info: '#3B82F6',
    infoBackground: '#EFF6FF',
    
    // Neutral palette for professional appearance
    background: '#FFFFFF',
    surface: '#F8FAFC',
    surfaceElevated: '#FFFFFF',
    border: '#E2E8F0',
    borderSubtle: '#F1F5F9',
    
    // High contrast text for accessibility (WCAG AA+)
    textPrimary: '#0F172A',
    textSecondary: '#475569',
    textDisabled: '#94A3B8',
    textInverse: '#FFFFFF',
    
    // Financial-specific states
    income: '#10B981',     // Green for positive cash flow
    expense: '#EF4444',    // Red for outgoing money
    transfer: '#8B5CF6',   // Purple for transfers
    pending: '#F59E0B',    // Amber for pending status
    approved: '#10B981',   // Green for approved
    rejected: '#EF4444'    // Red for rejected
  },
  
  typography: {
    fontFamily: {
      sans: ['Inter', '-apple-system', 'BlinkMacSystemFont', 'Segoe UI', 'Roboto', 'sans-serif'],
      mono: ['JetBrains Mono', 'Monaco', 'Consolas', 'Liberation Mono', 'Courier New', 'monospace']
    },
    fontSize: {
      xs: '0.75rem',    // 12px
      sm: '0.875rem',   // 14px
      base: '1rem',     // 16px
      lg: '1.125rem',   // 18px
      xl: '1.25rem',    // 20px
      '2xl': '1.5rem',  // 24px
      '3xl': '1.875rem',// 30px
      '4xl': '2.25rem'  // 36px
    },
    fontWeight: {
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700
    },
    lineHeight: {
      tight: 1.25,
      normal: 1.5,
      relaxed: 1.625
    }
  },
  
  spacing: {
    '0': '0',
    '1': '0.25rem',  // 4px
    '2': '0.5rem',   // 8px
    '3': '0.75rem',  // 12px
    '4': '1rem',     // 16px
    '5': '1.25rem',  // 20px
    '6': '1.5rem',   // 24px
    '8': '2rem',     // 32px
    '10': '2.5rem',  // 40px
    '12': '3rem',    // 48px
    '16': '4rem',    // 64px
    '20': '5rem',    // 80px
    '24': '6rem',    // 96px
    '32': '8rem'     // 128px
  },
  
  borderRadius: {
    none: '0',
    sm: '0.125rem',
    base: '0.25rem',
    lg: '0.5rem',
    xl: '0.75rem',
    full: '9999px'
  },
  
  boxShadow: {
    sm: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
    base: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
    lg: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
    xl: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)'
  },
  
  // v3.0 Glass Morphism Configuration
  glass: {
    primary: 'rgba(31, 111, 235, 0.15)',
    secondary: 'rgba(31, 111, 235, 0.08)',
    surface: 'rgba(248, 250, 252, 0.85)',
    border: 'rgba(226, 232, 240, 0.3)',
    blurIntensity: {
      light: 8,
      medium: 12,
      heavy: 24
    },
    saturation: 150,
    opacity: {
      subtle: 0.1,
      light: 0.3,
      medium: 0.5,
      heavy: 0.7,
      ultra: 0.85
    }
  },
  
  // v3.0 Generous Spacing (2x traditional)
  spacingV3: {
    none: '0px',
    xs: '8px',
    sm: '16px',
    md: '24px',
    lg: '32px',
    xl: '48px',
    '2xl': '64px',
    '3xl': '96px',
    cardPadding: '32px',
    buttonPadding: '16px 32px',
    inputPadding: '16px 20px',
    sectionMargin: '48px'
  },
  
  // v3.0 Radical Border Radius
  borderRadiusV3: {
    xs: '8px',
    sm: '12px',
    md: '16px',
    lg: '24px',
    xl: '32px',
    '2xl': '40px',
    '3xl': '48px',
    circle: '50%',
    pill: '9999px',
    card: '24px',
    button: '16px',
    input: '12px',
    dialog: '32px'
  },
  
  // v3.0 Enhanced Shadows with Glass Effects
  shadowV3: {
    none: 'none',
    sm: '0 1px 3px rgba(31, 111, 235, 0.08)',
    md: '0 4px 12px rgba(31, 111, 235, 0.08)',
    lg: '0 8px 24px rgba(31, 111, 235, 0.1)',
    xl: '0 16px 48px rgba(31, 111, 235, 0.12)',
    glass: '0 8px 32px rgba(31, 111, 235, 0.06)',
    floating: '0 12px 40px rgba(31, 111, 235, 0.15)',
    elevated: '0 20px 60px rgba(31, 111, 235, 0.18)',
    button: '0 4px 12px rgba(31, 111, 235, 0.1)',
    card: '0 8px 24px rgba(31, 111, 235, 0.08)',
    dialog: '0 24px 80px rgba(31, 111, 235, 0.2)',
    tooltip: '0 4px 16px rgba(31, 111, 235, 0.12)'
  },
  
  // v3.0 Smooth Animations
  animationV3: {
    duration: {
      instant: '0ms',
      fast: '150ms',
      base: '300ms',
      slow: '500ms',
      slower: '750ms',
      slowest: '1000ms'
    },
    easing: {
      linear: 'linear',
      smooth: 'cubic-bezier(0.4, 0, 0.2, 1)',
      snappy: 'cubic-bezier(0.4, 0, 0.2, 1)',
      bounce: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)',
      gentle: 'cubic-bezier(0.25, 0.8, 0.25, 1)',
      entrance: 'cubic-bezier(0, 0, 0.2, 1)',
      exit: 'cubic-bezier(0.4, 0, 1, 1)'
    },
    useGPU: true,
    respectReducedMotion: true
  },
  
  components: {
    currency: {
      positive: '#10B981',
      negative: '#EF4444',
      neutral: '#0F172A',
      fontSize: '1rem',
      fontFamily: 'JetBrains Mono, Monaco, Consolas, monospace',
      fontWeight: 600
    },
    
    statusBadge: {
      pending: { 
        bg: '#FFFBEB', 
        text: '#92400E', 
        border: '#FCD34D' 
      },
      approved: { 
        bg: '#ECFDF5', 
        text: '#065F46', 
        border: '#34D399' 
      },
      rejected: { 
        bg: '#FEF2F2', 
        text: '#991B1B', 
        border: '#F87171' 
      },
      transferred: { 
        bg: '#EFF6FF', 
        text: '#1E40AF', 
        border: '#60A5FA' 
      },
      received: { 
        bg: '#F0FDF4', 
        text: '#14532D', 
        border: '#22C55E' 
      }
    },
    
    dataTable: {
      headerBg: '#F8FAFC',
      headerText: '#374151',
      rowBg: '#FFFFFF',
      rowBgAlt: '#F9FAFB',
      rowBorder: '#F3F4F6',
      textAlign: 'right'
    },
    
    form: {
      inputBg: '#FFFFFF',
      inputBorder: '#D1D5DB',
      inputFocusBorder: '#1F6FEB',
      labelText: '#374151',
      errorText: '#EF4444',
      successText: '#10B981'
    },
    
    card: {
      bg: '#FFFFFF',
      border: '#E2E8F0',
      shadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
      headerBg: '#F8FAFC',
      padding: '1.5rem'
    }
  },
  
  labels: {
    es: {
      // Financial terms
      amount: 'Monto',
      balance: 'Saldo',
      income: 'Ingreso',
      expense: 'Gasto',
      transfer: 'Transferencia',
      date: 'Fecha',
      status: 'Estado',
      pending: 'Pendiente',
      approved: 'Aprobado',
      rejected: 'Rechazado',
      total: 'Total',
      subtotal: 'Subtotal',
      tax: 'Impuesto',
      
      // Actions
      save: 'Guardar',
      cancel: 'Cancelar',
      edit: 'Editar',
      delete: 'Eliminar',
      approve: 'Aprobar',
      reject: 'Rechazar',
      transferAction: 'Transferir',
      export: 'Exportar',
      print: 'Imprimir',
      
      // Navigation
      dashboard: 'Tablero',
      transactions: 'Transacciones',
      reports: 'Reportes',
      settings: 'Configuración',
      users: 'Usuarios',
      
      // Messages
      success: 'Éxito',
      error: 'Error',
      warning: 'Advertencia',
      loading: 'Cargando...',
      noData: 'No hay datos disponibles'
    },
    
    en: {
      // Financial terms
      amount: 'Amount',
      balance: 'Balance',
      income: 'Income',
      expense: 'Expense',
      transfer: 'Transfer',
      date: 'Date',
      status: 'Status',
      pending: 'Pending',
      approved: 'Approved',
      rejected: 'Rejected',
      total: 'Total',
      subtotal: 'Subtotal',
      tax: 'Tax',
      
      // Actions
      save: 'Save',
      cancel: 'Cancel',
      edit: 'Edit',
      delete: 'Delete',
      approve: 'Approve',
      reject: 'Reject',
      transferAction: 'Transfer',
      export: 'Export',
      print: 'Print',
      
      // Navigation
      dashboard: 'Dashboard',
      transactions: 'Transactions',
      reports: 'Reports',
      settings: 'Settings',
      users: 'Users',
      
      // Messages
      success: 'Success',
      error: 'Error',
      warning: 'Warning',
      loading: 'Loading...',
      noData: 'No data available'
    }
  }
}

// Helper functions for using the theme
export function getFinancialColor(type: 'income' | 'expense' | 'transfer', theme = financialTheme) {
  // Fix object injection by using explicit property access
  switch (type) {
    case 'income': return theme.colors.income
    case 'expense': return theme.colors.expense
    case 'transfer': return theme.colors.transfer
    default: return theme.colors.income
  }
}

export function getStatusBadgeStyles(status: 'pending' | 'approved' | 'rejected' | 'transferred' | 'received', theme = financialTheme) {
  // Fix object injection by using explicit property access
  switch (status) {
    case 'pending': return theme.components.statusBadge.pending
    case 'approved': return theme.components.statusBadge.approved
    case 'rejected': return theme.components.statusBadge.rejected
    case 'transferred': return theme.components.statusBadge.transferred
    case 'received': return theme.components.statusBadge.received
    default: return theme.components.statusBadge.pending
  }
}

export function getFinancialLabel(key: string, language: 'es' | 'en' = 'es', theme = financialTheme) {
  // Fix object injection by using explicit property access
  const labels = language === 'es' ? theme.labels.es : theme.labels.en
  // eslint-disable-next-line security/detect-object-injection
  return (labels as Record<string, string>)[key] || key
}

// CSS-in-JS theme object for styled-components or emotion
export const financialThemeCSS = {
  '--birhaus-primary': financialTheme.colors.primary,
  '--birhaus-primary-hover': financialTheme.colors.primaryHover,
  '--birhaus-success': financialTheme.colors.success,
  '--birhaus-warning': financialTheme.colors.warning,
  '--birhaus-error': financialTheme.colors.error,
  '--birhaus-info': financialTheme.colors.info,
  '--birhaus-background': financialTheme.colors.background,
  '--birhaus-surface': financialTheme.colors.surface,
  '--birhaus-border': financialTheme.colors.border,
  '--birhaus-text-primary': financialTheme.colors.textPrimary,
  '--birhaus-text-secondary': financialTheme.colors.textSecondary,
  '--birhaus-income': financialTheme.colors.income,
  '--birhaus-expense': financialTheme.colors.expense,
  '--birhaus-transfer': financialTheme.colors.transfer,
  
  '--birhaus-font-sans': financialTheme.typography.fontFamily.sans.join(', '),
  '--birhaus-font-mono': financialTheme.typography.fontFamily.mono.join(', '),
  
  '--birhaus-radius-sm': financialTheme.borderRadius.sm,
  '--birhaus-radius-base': financialTheme.borderRadius.base,
  '--birhaus-radius-lg': financialTheme.borderRadius.lg,
  
  '--birhaus-shadow-sm': financialTheme.boxShadow.sm,
  '--birhaus-shadow-base': financialTheme.boxShadow.base,
  '--birhaus-shadow-lg': financialTheme.boxShadow.lg
}

export { financialTheme as default }